/**
 * PrestudyUpload — Authenticated document upload for pre-survey materials.
 *
 * Users with facility access can upload policies, logs, certificates,
 * and other documents required before a site visit survey.
 *
 * Route: /visits/:visitId/prestudy/upload
 */

import { useState, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, Upload, FileText, CheckCircle, AlertCircle } from 'lucide-react'
import { FieldAppShell } from '../../components/survey/FieldAppShell'
import {
  uploadPrestudyDocument,
  PRESTUDY_DOC_CATEGORIES,
  type PrestudyDocType,
} from '../../lib/services/prestudy'

export function PrestudyUpload() {
  const { visitId } = useParams<{ visitId: string }>()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [docType, setDocType] = useState<PrestudyDocType>('policy')
  const [label, setLabel] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  if (!visitId) {
    return (
      <FieldAppShell eyebrow="Survey" title="Upload Pre-Survey Document" backTo="/" backLabel="Home">
        <div className="px-4 py-6">
          <p className="text-sm text-slate-500">Invalid visit.</p>
          <Link to="/" className="btn-ghost text-sm mt-4 inline-block">Home</Link>
        </div>
      </FieldAppShell>
    )
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0]
    if (selected) {
      setFile(selected)
      if (!label) {
        setLabel(selected.name.replace(/\.[^.]+$/, ''))
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file || !label.trim()) return

    setUploading(true)
    setError(null)

    try {
      await uploadPrestudyDocument(visitId, {
        doc_type: docType,
        label: label.trim(),
        file,
      })
      setSuccess(true)
      setFile(null)
      setLabel('')
      if (fileInputRef.current) fileInputRef.current.value = ''
    } catch (err: any) {
      setError(err.message || 'Upload failed. Please try again.')
    } finally {
      setUploading(false)
    }
  }

  return (
    <FieldAppShell eyebrow="Survey" title="Upload Pre-Survey Document" backTo={`/visits/${visitId}/survey/documents`} backLabel="Back to Documents">
    <div className="space-y-4 px-4 py-6 max-w-lg mx-auto">
      <div className="flex items-center gap-2">
        <Link to={`/visits/${visitId}/survey/documents`} className="text-slate-400 hover:text-slate-600">
          <ArrowLeft size={20} />
        </Link>
        <h2 className="section-title">Upload Pre-Survey Document</h2>
      </div>

      {success && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2 text-sm text-emerald-700">
          <CheckCircle size={16} />
          Document uploaded successfully.
          <button
            type="button"
            onClick={() => setSuccess(false)}
            className="ml-auto text-xs font-bold underline"
          >
            Upload another
          </button>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-sm text-red-700">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Document type */}
        <div className="app-surface p-4 space-y-3">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
            Document Type
          </label>
          <div className="grid grid-cols-1 gap-2">
            {PRESTUDY_DOC_CATEGORIES.map((cat) => (
              <button
                key={cat.type}
                type="button"
                onClick={() => setDocType(cat.type)}
                className={`text-left px-3 py-2.5 rounded-lg border text-sm transition-colors ${
                  docType === cat.type
                    ? 'border-[var(--brand-primary)] bg-blue-50 text-[var(--brand-primary)] font-semibold'
                    : 'border-slate-200 hover:border-slate-300 text-slate-700'
                }`}
              >
                <span className="font-semibold">{cat.label}</span>
                <p className="text-[10px] text-slate-400 mt-0.5">{cat.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Label */}
        <div className="app-surface p-4 space-y-2">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
            Document Label
          </label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g., Infection Control Policy 2026"
            className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-[var(--brand-primary)] focus:outline-none focus:ring-2 focus:ring-blue-100"
          />
        </div>

        {/* File picker */}
        <div className="app-surface p-4 space-y-3">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
            File
          </label>
          {file ? (
            <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
              <FileText size={20} className="text-[var(--brand-primary)]" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-700 truncate">{file.name}</p>
                <p className="text-[10px] text-slate-400">
                  {(file.size / 1024).toFixed(0)} KB — {file.type || 'unknown type'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setFile(null)
                  if (fileInputRef.current) fileInputRef.current.value = ''
                }}
                className="text-xs text-red-500 font-bold"
              >
                Remove
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="w-full py-8 border-2 border-dashed border-slate-300 rounded-lg text-center hover:border-[var(--brand-primary)] transition-colors"
            >
              <Upload size={24} className="mx-auto text-slate-400 mb-2" />
              <p className="text-sm text-slate-500">Tap to select a file</p>
              <p className="text-[10px] text-slate-400 mt-1">PDF, images, or documents up to 50MB</p>
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            onChange={handleFileSelect}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.heic"
            className="hidden"
          />
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={uploading || !file || !label.trim()}
          className="btn-primary w-full py-3 text-sm disabled:opacity-50"
        >
          {uploading ? 'Uploading…' : 'Upload Document'}
        </button>
      </form>
    </div>
    </FieldAppShell>
  )
}
